import { checkRequiredParameter, handleReservationAcceptance, isWorkerOffline } from "../../ActionUtils";
import {
    handleConversationInitialization,
    handleGetConversationBySid
} from "~/modules/Conversation/ConversationErrorUtils";
import { Reservation, Supervisor, Task } from "~/packages/taskrouter";
import { CreateCbmTaskResponse, CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, Logger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { PreviewOutboundServiceImpl } from "~/backend/generated/PreviewFlex/api/previewOutbound.service";
import { OutboundWorkflowAndQueueResponse } from "~/backend/generated/PreviewFlex/model/outboundWorkflowAndQueueResponse";
import { OutboundAddressConfiguration } from "~/backend/generated/PreviewFlex/model/outboundAddressConfiguration";
import { OutboundTaskHandler } from "~/modules/actions/OutboundTaskHandler";
import { Conversation } from "~/modules/Conversation";
import { ConversationsSdkImpl } from "~/modules/conversations/ConversationsSdkImpl";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

interface InitializationResult {
    context: ContextManager;
    taskRouter: TaskRouterImpl;
    conversations: ConversationsSdkImpl;
    cbm: CbmImpl;
    previewOutboundServiceImpl: PreviewOutboundServiceImpl;
    logger: Logger;
    analytics: AnalyticsImpl;
    accountConfig: AccountConfigDataContainer;
    worker: Supervisor;
    attributesForTaskCreation: object | undefined;
    conversationInitPromise: Promise<void>;
}




export interface StartOutboundEmailTaskOptions {
    from?: string;
    fromName?: string;
    taskQueueSid?: string;
    workflowSid?: string;
    attributesForTaskCreation?: object;
}




export interface StartOutboundEmailTaskResponse {
    task: Task;
    conversation: Conversation;
}








































export class StartOutboundEmailTask implements Action<Promise<StartOutboundEmailTaskResponse>> {
    


    readonly #to: string;

    


    readonly #options?: StartOutboundEmailTaskOptions;

    constructor(to: string, options?: StartOutboundEmailTaskOptions) {
        this.#to = to;
        this.#options = options;
    }

    async run(ctx: {}): Promise<StartOutboundEmailTaskResponse> {
        const context = ctx as ContextManager;
        const initResult = await this.#initializeContext(context);
        const { workflowSid, taskQueueSid } = await this.#resolveWorkflowAndTaskQueue(initResult);
        const fromParam = await this.#resolveFromParameter(initResult, workflowSid, taskQueueSid);

        return this.#createTaskAndReservation(initResult, workflowSid, taskQueueSid, fromParam);
    }

    async #initializeContext(context: ContextManager): Promise<InitializationResult> {
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        const conversations = context.retrieveInstanceOf(ContextInstance.ConversationsSdkImpl, ConversationsSdkImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        const previewOutboundServiceImpl = context.retrieveInstanceOf(
            ContextInstance.PreviewOutboundServiceImpl,
            PreviewOutboundServiceImpl
        );

        await cbm.init();
        await taskRouter.init();

        const logger = getLogger(context)("StartOutboundEmailTask");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`StartOutboundEmailTask invoked to address: ${this.#to}`);

        const worker = taskRouter.worker;
        const { attributesForTaskCreation } = this.#options ?? {};

        const conversationInitPromise = handleConversationInitialization(conversations, {
            actionName: "StartOutboundEmailTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartOutboundEmailTaskCompleted,
            extra: {},
            logger
        });

        this.#validateWorkerAndConfig(context, worker, analytics, accountConfig, logger);

        checkRequiredParameter(this.#to, "to", {
            actionName: "StartOutboundEmailTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartOutboundEmailTaskCompleted,
            extra: { to: this.#to },
            logger
        });

        return {
            context,
            taskRouter,
            conversations,
            cbm,
            previewOutboundServiceImpl,
            logger,
            analytics,
            accountConfig,
            worker,
            attributesForTaskCreation,
            conversationInitPromise
        };
    }

    #validateWorkerAndConfig(
        context: ContextManager,
        worker: Supervisor,
        analytics: AnalyticsImpl,
        accountConfig: AccountConfigDataContainer,
        logger: Logger
    ) {
        if (!worker) {
            const errorMsg = `StartOutboundEmailTask: worker is not initialized`;
            logger.error(errorMsg);
            throwFlexSdkError(context)(ErrorCode.WorkerNotInitialized, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const accountConfigContainer = accountConfig.get();
        const taskrouterOfflineActivitySid = accountConfigContainer.taskrouterOfflineActivitySid;
        if (!taskrouterOfflineActivitySid) {
            const errorMsg =
                "StartOutboundEmailTask: taskrouterOfflineActivitySid is undefined in account configuration";
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throwFlexSdkError(context)(
                ErrorCode.TaskrouterOfflineActivitySidNotConfigured,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        if (isWorkerOffline(worker, taskrouterOfflineActivitySid)) {
            const errorMsg = `StartOutboundEmailTask: Worker is offline, outbound email task cancelled`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            throwFlexSdkError(context)(
                ErrorCode.WorkerOfflineEmailTaskCancelled,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }
    }

    async #resolveWorkflowAndTaskQueue(initResult: InitializationResult) {
        const { previewOutboundServiceImpl, accountConfig, analytics, worker, logger, context } = initResult;

        let outboundWorkflowAndQueue: OutboundWorkflowAndQueueResponse | undefined;
        if (!this.#options?.workflowSid || !this.#options?.taskQueueSid) {
            let accountSid = "";
            try {
                accountSid = accountConfig.get().accountSid;
                outboundWorkflowAndQueue = await previewOutboundServiceImpl.getOutboundWorkflowAndQueue(accountSid);
            } catch (error: unknown) {
                const errorMsg =
                    `StartOutboundEmailTask: Failed to get outbound email workflow settings, ` +
                    `make sure Email settings are configured in Twilio Console`;
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                    error: `${errorMsg} (${toCauseMessage(error)})`
                });
                throwFlexSdkError(context)(
                    ErrorCode.FailedToRetrieveOutboundEmailSettings,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                );
            }
        }

        const workflowSid = this.#options?.workflowSid ?? outboundWorkflowAndQueue?.email?.workflow_sid;
        if (!workflowSid) {
            const errorMsg =
                `StartOutboundEmailTask: outbound email workflow is not configured in Twilio Console. ` +
                `Please configure or provide 'workflowSid' in options`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, { error: errorMsg });
            return throwFlexSdkError(context)(
                ErrorCode.OutboundEmailWorkflowSidUndefined,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        const taskQueueSid = this.#options?.taskQueueSid ?? outboundWorkflowAndQueue?.email?.queue_sid;
        if (!taskQueueSid) {
            const errorMsg =
                `StartOutboundEmailTask: Outbound email queue is not configured in Twilio Console. ` +
                `Please configure or provide 'taskQueueSid' in options`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.OutboundEmailQueueSidUndefined,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return { workflowSid, taskQueueSid };
    }

    async #resolveFromParameter(initResult: InitializationResult, workflowSid: string, taskQueueSid: string) {
        const { previewOutboundServiceImpl, accountConfig, analytics, worker, logger, context } = initResult;

        let outboundAddressConfiguration: OutboundAddressConfiguration | undefined;
        if (!this.#options?.from) {
            let accountSid = "";
            try {
                accountSid = accountConfig.get().accountSid;
                outboundAddressConfiguration = await previewOutboundServiceImpl.getOutboundAddressConfiguration(
                    accountSid
                );
            } catch (error: unknown) {
                const errorMsg =
                    `StartOutboundEmailTask: Failed to get outbound email workflow settings, ` +
                    `make sure Email settings are configured in Twilio Console`;
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                    error: `${errorMsg} (${toCauseMessage(error)})`,
                    workflowSid,
                    taskQueueSid
                });
                throwFlexSdkError(context)(
                    ErrorCode.FailedToRetrieveOutboundEmailSettings,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                );
            }
        }

        const fromParam = this.#options?.from ?? outboundAddressConfiguration?.email?.coordinates?.address;
        if (!fromParam) {
            const errorMsg =
                `StartOutboundEmailTask: Default outbound email address is not configured in Twilio Console. ` +
                `Please configure or provide 'from' in options`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: errorMsg,
                workflowSid,
                taskQueueSid
            });
            throwFlexSdkError(context)(
                ErrorCode.OutboundEmailFromParameterUndefined,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return fromParam;
    }

    async #createTaskAndReservation(
        initResult: InitializationResult,
        workflowSid: string,
        taskQueueSid: string,
        fromParam?: string | null
    ): Promise<StartOutboundEmailTaskResponse> {
        const {
            cbm,
            worker,
            analytics,
            logger,
            context,
            conversations,
            conversationInitPromise,
            attributesForTaskCreation
        } = initResult;

        const outboundAttributes = { ...attributesForTaskCreation, direction: "outbound" };

        const outboundTaskHandler = new OutboundTaskHandler(worker);
        outboundTaskHandler.startListeningReservations();

        let createCbmTaskResponse: CreateCbmTaskResponse;
        try {
            createCbmTaskResponse = await cbm.createEmailTask(worker, workflowSid, taskQueueSid, {
                attributes: { customerAddress: this.#to, ...outboundAttributes },
                to: this.#to,
                from: fromParam!,
                fromName: this.#options?.fromName,
                taskUniqueChannelName: "email"
            });
        } catch (error) {
            const errorMsg = `StartOutboundEmailTask: Failed to create outbound email task`;
            outboundTaskHandler.stopListeningReservations();
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                workflowSid,
                taskQueueSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToCreateOutboundEmailTask,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }

        let reservation: Reservation;
        try {
            reservation = await outboundTaskHandler.waitForTaskReservation(createCbmTaskResponse.taskSid);
        } catch (error: unknown) {
            const errorMsg = `StartOutboundEmailTask: Failed to create reservation for outbound email task ${createCbmTaskResponse.taskSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.StartOutboundEmailTaskCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                workflowSid,
                taskQueueSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToCreateOutboundEmailTaskReservation,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }

        reservation = await handleReservationAcceptance(cbm, reservation, {
            actionName: "StartOutboundEmailTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartOutboundEmailTaskCompleted,
            extra: {
                workflowSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid,
                reservationSid: reservation.sid
            },
            logger
        });

        const conversationSid = reservation.task.attributes.conversationSid as string;
        await conversationInitPromise;

        const conversation = await handleGetConversationBySid(conversations, conversationSid, context, {
            actionName: "StartOutboundEmailTask",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.ResumeConversationCompleted,
            extra: {
                conversationSid,
                workflowSid,
                taskSid: createCbmTaskResponse.taskSid,
                interactionSid: createCbmTaskResponse.interactionSid,
                reservationSid: reservation.sid
            },
            logger
        });

        return { task: reservation.task, conversation };
    }
}
